import { getEnv } from "../lib/env";

const env = getEnv();

function buildApiUrl(path: string): string {
  const baseUrl = env.apiBaseUrl.trim();

  if (!baseUrl) {
    return path;
  }

  if ((baseUrl === "/api" || baseUrl === "/api/") && path.startsWith("/api/")) {
    return path;
  }

  if (baseUrl.endsWith("/") && path.startsWith("/")) {
    return `${baseUrl.slice(0, -1)}${path}`;
  }

  if (!baseUrl.endsWith("/") && !path.startsWith("/")) {
    return `${baseUrl}/${path}`;
  }

  return `${baseUrl}${path}`;
}

export class ApiError extends Error {
  status: number;
  code: string | null;
  debug: Record<string, unknown> | null;

  constructor(
    method: string,
    path: string,
    status: number,
    message?: string,
    code?: string | null,
    debug?: Record<string, unknown> | null
  ) {
    super(message ?? `${method} ${path} failed: ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = code ?? null;
    this.debug = debug ?? null;
  }
}

async function apiRequest<TResponse>(method: string, path: string, body?: unknown, extraHeaders: Record<string, string> = {}): Promise<TResponse> {
  const requestInit: RequestInit = {
    method,
    credentials: "include",
    ...(Object.keys(extraHeaders).length > 0 ? { headers: extraHeaders } : {})
  };

  if (body !== undefined) {
    requestInit.headers = { "Content-Type": "application/json", ...extraHeaders };
    requestInit.body = JSON.stringify(body);
  }

  const response = await fetch(buildApiUrl(path), requestInit);

  if (!response.ok) {
    let payload:
      | {
          code?: string;
          message?: string;
          debug?: Record<string, unknown>;
        }
      | null = null;

    try {
      payload = (await response.json()) as {
        code?: string;
        message?: string;
        debug?: Record<string, unknown>;
      };
    } catch {
      payload = null;
    }

    throw new ApiError(
      method,
      path,
      response.status,
      payload?.message,
      payload?.code ?? null,
      payload?.debug ?? null
    );
  }

  return response.json() as Promise<TResponse>;
}

export async function apiPostWithHeaders<TRequest, TResponse>(
  path: string,
  body: TRequest,
  headers: Record<string, string>
): Promise<TResponse> {
  return apiRequest<TResponse>("POST", path, body, headers);
}

export async function apiPutWithHeaders<TRequest, TResponse>(path: string, body: TRequest, headers: Record<string, string>): Promise<TResponse> {
  return apiRequest<TResponse>("PUT", path, body, headers);
}

export async function apiPatchWithHeaders<TRequest, TResponse>(path: string, body: TRequest, headers: Record<string, string>): Promise<TResponse> {
  return apiRequest<TResponse>("PATCH", path, body, headers);
}

export async function apiGet<T>(path: string): Promise<T> {
  return apiRequest<T>("GET", path);
}

export async function apiPost<TRequest, TResponse>(path: string, body: TRequest): Promise<TResponse> {
  return apiRequest<TResponse>("POST", path, body);
}

export async function apiPatch<TRequest, TResponse>(path: string, body: TRequest): Promise<TResponse> {
  return apiRequest<TResponse>("PATCH", path, body);
}

export async function apiDelete<TRequest, TResponse>(path: string, body?: TRequest): Promise<TResponse> {
  return apiRequest<TResponse>("DELETE", path, body);
}
